import axios from 'axios';
import TokenManager from './tokenManager';

// Create axios instance with base configuration
const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api';

console.log('baseURL env:', process.env.REACT_APP_API_BASE_URL);

const api = axios.create({
  baseURL,
  timeout: 30000, // 30 seconds
});

// Global token manager instance (will be initialized when MSAL is ready)
let tokenManager = null;

// Function to initialize token manager with MSAL instance
export const initializeTokenManager = (msalInstance) => {
  if (!tokenManager) {
    tokenManager = new TokenManager(msalInstance);
  }
  return tokenManager;
};

// Get current token manager instance
export const getTokenManager = () => tokenManager;

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Skip token for non-API requests or if token manager is not initialized
    if (!tokenManager) {
      const fallbackToken = localStorage.getItem('msal.token');
      if (fallbackToken) {
        config.headers.Authorization = `Bearer ${fallbackToken}`;
      }
      return config;
    }

    try {
      // Ensure we have a valid token before making the request
      const token = await tokenManager.ensureValidToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error ensuring valid token:', error);
      // Continue with the request even if token refresh fails
      // The response interceptor will handle 401 errors
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


// Response interceptor to handle common errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors with token refresh retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.warn('API 401 received, attempting token refresh and retry');

      // Mark this request as retried to prevent infinite loops
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        if (tokenManager) {
          const newToken = await tokenManager.refreshToken();

          if (newToken) {
            // Update the authorization header with the new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            // Retry the original request with the new token
            return api(originalRequest);
          }
        } else {
          console.warn('TokenManager not available for 401 retry');
        }
      } catch (refreshError) {
        console.error('Token refresh failed during 401 retry:', refreshError);

        // Re-throw the original error if refresh fails
        return Promise.reject(error);
      }
    }

    // Handle other errors normally
    if (error.response?.status === 401) {
      console.warn('API 401 received after retry attempt. Not redirecting to avoid auth loops.');
    }

    return Promise.reject(error);
  }
);

// Campaigns API
export const campaignsAPI = {
  getAll: () => api.get('/campaigns'),
  getById: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.patch(`/campaigns/${id}`, data),
  uploadCVs: (campaignId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/campaigns/${campaignId}/candidates/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getCandidates: (campaignId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/campaigns/${campaignId}/candidates${queryString ? `?${queryString}` : ''}`);
  },
  exportCandidates: (campaignId) => api.post(`/campaigns/${campaignId}/candidates/export`, {}, {
    responseType: 'blob'
  })
};

// Candidates API
export const candidatesAPI = {
  getById: (id) => api.get(`/candidates/${id}`),
  update: (id, data) => api.patch(`/candidates/${id}`, data),
  delete: (id) => api.delete(`/candidates/${id}`),
  uploadProfilePhoto: (id, file) => {
    const formData = new FormData();
    formData.append('profilePhoto', file);
    return api.post(`/candidates/${id}/profile-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getProfilePhoto: async (id, { cacheBust } = {}) => {
    const url = cacheBust ? `/candidates/${id}/profile-photo/url?cb=${encodeURIComponent(cacheBust)}` : `/candidates/${id}/profile-photo/url`;
    const resp = await api.get(url);
    return resp;
  },
  getProfilePhotosBulk: async (ids, { ttl } = {}) => {
    const resp = await api.post(`/candidates/profile-photos/urls`, { ids, ttl });
    return resp;
  },
  deleteProfilePhoto: (id) => api.delete(`/candidates/${id}/profile-photo`)
};

// CV Files API
export const cvFilesAPI = {
  download: (id) => api.get(`/cvs/${id}/download`, { responseType: 'blob' }),
  view: (id) => api.get(`/cvs/${id}/view`, { responseType: 'blob' }),
  getInfo: (id) => api.get(`/cvs/${id}/info`)
};

export default api;
